import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { downloadCsv } from "../utils/csv";
import { useToast } from "../components/ToastProvider";
import { Eye } from "lucide-react";
import BookingDetailsModal from "../components/BookingDetailsModal";

const EMPTY = [];

function StatCard({ label, value, sub }) {
  return (
    <div className="rs-card rs-stat">
      <div className="rs-stat__label">{label}</div>
      <div className="rs-stat__value">{value}</div>
      {sub ? <div className="rs-stat__sub">{sub}</div> : null}
    </div>
  );
}

function StackedBarChart({ data }) {
  const width = 920;
  const height = 240;
  const padding = 28;

  const points = useMemo(() => {
    if (!data || data.length === 0) return [];
    const max = Math.max(...data.map((d) => Number(d.totalCount || 0)), 1);

    const innerW = width - padding * 2;
    const innerH = height - padding * 2;
    const barW = innerW / data.length;

    return data.map((d, i) => {
      const total = Number(d.totalCount || 0);
      const paid = Number(d.paidCount || 0);
      const incomplete = Number(d.incompleteCount || 0);

      const x = padding + i * barW + barW * 0.15;
      const w = barW * 0.7;

      const totalH = (total / max) * innerH;
      const paidH = (paid / max) * innerH;
      const incompleteH = (incomplete / max) * innerH;

      return {
        key: d.day,
        x,
        w,
        yTotal: padding + (innerH - totalH),
        totalH,
        paidH,
        incompleteH,
        label: String(d.day).slice(5),
        showLabel: i % Math.ceil(data.length / 6) === 0 || i === data.length - 1,
        total,
      };
    });
  }, [data]);

  return (
    <div className="rs-card">
      <div className="rs-card__header">
        <div className="rs-card__title">Bookings (last 30 days)</div>
        <div className="rs-card__subtitle">Paid vs incomplete</div>
      </div>
      <div className="rs-chart">
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Bookings chart">
          <rect x="0" y="0" width={width} height={height} fill="transparent" />

          <line
            x1={padding}
            y1={height - padding}
            x2={width - padding}
            y2={height - padding}
            stroke="rgba(148,163,184,0.5)"
            strokeWidth="1"
          />

          {points.map((p) => (
            <g key={p.key}>
              <rect
                x={p.x}
                y={height - padding - p.incompleteH}
                width={p.w}
                height={p.incompleteH}
                fill="#f59e0b"
                rx="6"
              />
              <rect
                x={p.x}
                y={height - padding - p.incompleteH - p.paidH}
                width={p.w}
                height={p.paidH}
                fill="#3b82f6"
                rx="6"
              />
              {p.showLabel ? <text
                x={p.x + p.w / 2}
                y={height - 8}
                textAnchor="middle"
                fontSize="10"
                fill="#64748b"
              >
                {p.label}
              </text> : null}
            </g>
          ))}
        </svg>
      </div>
      <div className="rs-legend">
        <div className="rs-legend__item">
          <span className="rs-dot rs-dot--blue" /> Paid
        </div>
        <div className="rs-legend__item">
          <span className="rs-dot rs-dot--amber" /> Incomplete
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [detailsBooking, setDetailsBooking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/admin/dashboard");
      setData(response?.data?.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetails = async (serviceId) => {
    if (!serviceId) return;
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsError("");
    setDetailsBooking(null);

    try {
      const response = await api.get(`/api/admin/bookings/${serviceId}`);
      setDetailsBooking(response?.data?.data || null);
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to load booking details";
      setDetailsError(message);
      toast.error(message);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsLoading(false);
    setDetailsError("");
    setDetailsBooking(null);
  };

  const saveDetails = async (payload) => {
    if (!detailsBooking?.serviceId) throw new Error("No booking selected");

    const response = await api.put(`/api/admin/bookings/${detailsBooking.serviceId}`, payload);
    const updated = response?.data?.data || null;
    if (updated) {
      setDetailsBooking(updated);
    }
    toast.success("Booking updated.");
    await load();
    return updated;
  };

  const totals = data?.totals || {};
  const bookingsByDay = data?.bookingsByDay || [];
  const recent = data?.recentBookings ?? EMPTY;

  const recentCsvRows = useMemo(
    () =>
      recent.map((r) => ({
        serviceId: r.serviceId,
        step: r.step,
        client: `${r.firstName || ""} ${r.lastName || ""}`.trim(),
        email: r.email,
        phone: r.phone,
        bedrooms: r.bedrooms,
        bathrooms: r.bathrooms,
        carpetOptions: r.carpetOptions || "",
        preferredDate: r.preferredDate,
        preferredTime: r.preferredTime,
        creationDate: r.creationDate,
      })),
    [recent]
  );

  return (
    <div className="rs-page rs-dashboard">
      <div className="rs-page__header">
        <div>
          <h1 className="rs-h1">Dashboard</h1>
          <div className="rs-muted">Overview of bookings and jobs</div>
        </div>
        <button className="rs-btn rs-btn--outline" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="rs-grid rs-grid--stats">
        <StatCard label="Total Bookings" value={totals.total ?? 0} />
        <StatCard label="Paid (Step 3)" value={totals.paid ?? 0} sub="Payment completed" />
        <StatCard label="Incomplete (Step 2)" value={totals.incomplete ?? 0} sub="Details filled, unpaid" />
        <StatCard label="Assigned Jobs" value={totals.assignedPaid ?? 0} />
        <StatCard label="Unassigned Jobs" value={totals.unassignedPaid ?? 0} />
        <StatCard label="Finished Jobs" value={totals.finished ?? 0} />
      </div>

      <div className="rs-grid rs-grid--two">
        <StackedBarChart data={bookingsByDay} />

        <div className="rs-card">
          <div className="rs-card__header">
            <div className="rs-card__title">System</div>
            <div className="rs-card__subtitle">Active accounts</div>
          </div>
          <div className="rs-kv">
            <div className="rs-kv__row">
              <div className="rs-kv__k">Active employees</div>
              <div className="rs-kv__v">{totals.employeesActive ?? 0}</div>
            </div>
            <div className="rs-kv__row">
              <div className="rs-kv__k">Active users</div>
              <div className="rs-kv__v">{totals.usersActive ?? 0}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="rs-card">
        <div className="rs-card__header rs-card__header--row">
          <div>
            <div className="rs-card__title">Recent bookings</div>
            <div className="rs-card__subtitle">Latest 10 records</div>
          </div>
          <button
            className="rs-btn rs-btn--outline"
            onClick={() => downloadCsv("recent-bookings.csv", recentCsvRows)}
            disabled={recentCsvRows.length === 0}
          >
            Download CSV
          </button>
        </div>

        <div className="rs-table-wrap">
          <table className="rs-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Status</th>
                <th>Client</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Carpet</th>
                <th>Bathrooms</th>
                <th>Preferred</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr>
                  <td colSpan={9} className="rs-empty">
                    No data
                  </td>
                </tr>
              ) : (
                recent.map((r) => (
                  <tr key={r.serviceId}>
                    <td data-label="ID">
                      <div className="rs-td__value">{r.serviceId}</div>
                    </td>
                    <td data-label="Status">
                      <div className="rs-td__value">
                        {Number(r.step) === 3 ? (
                          <span className="rs-pill rs-pill--success">Paid</span>
                        ) : (
                          <span className="rs-pill rs-pill--warning">Incomplete</span>
                        )}
                      </div>
                    </td>
                    <td data-label="Client">
                      <div className="rs-td__value">{`${r.firstName || ""} ${r.lastName || ""}`.trim()}</div>
                    </td>
                    <td data-label="Email">
                      <div className="rs-td__value">{r.email}</div>
                    </td>
                    <td data-label="Phone">
                      <div className="rs-td__value">{r.phone}</div>
                    </td>
                    <td data-label="Carpet">
                      <div className="rs-td__value">{r.carpetOptions || <span className="rs-muted">-</span>}</div>
                    </td>
                    <td data-label="Bathrooms">
                      <div className="rs-td__value">
                        {typeof r.bathrooms !== "undefined" && r.bathrooms !== null ? r.bathrooms : <span className="rs-muted">-</span>}
                      </div>
                    </td>
                    <td data-label="Preferred">
                      <div className="rs-td__value">
                        {r.preferredDate ? `${r.preferredDate} ${r.preferredTime || ""}` : "-"}
                      </div>
                    </td>
                    <td data-label="View">
                      <div className="rs-td__value">
                        <button
                          type="button"
                          className="rs-icon-btn rs-icon-btn--sm"
                          onClick={() => openDetails(r.serviceId)}
                          aria-label={`View booking ${r.serviceId}`}
                          title="View details"
                        >
                          <Eye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <BookingDetailsModal
        open={detailsOpen}
        booking={detailsBooking}
        loading={detailsLoading}
        error={detailsError}
        onClose={closeDetails}
        onSave={saveDetails}
      />
    </div>
  );
}
